<template>
  <div class="registry-study-grid">
    <b-row class="search-block  mb-3 mt-1">
      <b-col
        xl="8"
        lg="7"
        md="7"
        sm="12"
        class="d-flex"
      >
        <searcher-module-entity-selection
          :search-patient-with-name="false"
          :identifier-options="entitySelectorOptionsForSearch"
          :initial-identifier="entitySelectorOptionsForSearch[0]"
          class="w-sm-100"
          @sampleSearch="onSampleSearch($event)"
          @studySearch="onStudySearch($event)"
          @patientSearch="onPatientSearch($event)"
        />
      </b-col>
      <b-col
        xl="4"
        lg="5"
        md="5"
        sm="12"
        class="text-right"
      >
        <div style="margin-top: 5px;">
          <b-button
            v-shortkey="['alt', 'r']"
            qa="registry-case-grid-reports-button"
            class="btn__link px-3"
            @shortkey.prevent="openReportsModal"
            @click="openReportsModal"
          >
            <i
              class="far fa-copy mr-2"
              aria-hidden="true"
            />{{ $tc('registry.btn.reports') }}
          </b-button>
          <b-button
            v-shortkey="['f10']"
            qa="registry-case-grid-patients-button"
            class="btn__link px-3"
            @shortkey.prevent="openPatientModal"
            @click="openPatientModal"
          >
            <i
              class="far fa-users mr-2"
              aria-hidden="true"
            />{{ $tc('global.patients') }}
          </b-button>
          <b-button
            v-if="$_canCreateCase()"
            id="requestsRouteToStudyRequestCreate"
            v-shortkey="['alt', 'n']"
            qa="registry-case-grid-create-case-button"
            class="ml-2 btn btn--primary btn-secondary btn-sm"
            @click="openCreateCaseModal(false)"
            @shortkey.prevent="openCreateCaseModal(false)"
          >
            <fa-icon :icon="['fal', 'plus']" /> {{ $tc('global.case',1) }}
          </b-button>
        </div>
      </b-col>
    </b-row>
    <div class="grid-container">
      <GridHeader
        :gridtitle="sideBarState.selectedOption ? $t(sideBarState.selectedOption.label) : ''"
        :gridsubtitle="msgSelectedRows"
        :qa="`${sideBarState.selectedOption && sideBarState.selectedOption.id}`"
      >
        <grid-display-options
          v-if="gridApi"
          :ui-component="displayOptionsUiComponent"
          @changedPageSize="changedPageSize($event)"
          @changedMode="changedMode($event)"
        />
        <div class="pl-1 pr-0 d-flex align-items-center">
          <button
            id="samplesCleanFilters"
            v-tooltip="$t('global.cleanFilter')"
            v-shortkey="['ctrl', 'alt', 'f']"
            qa="registry-case-grid-clean-button"
            type="button"
            class="btn btn__icon btn__icon--primary mx-2 p-0 btn-secondary"
            @shortkey.prevent="cleanFilters"
            @click="cleanFilters"
          >
            <i
              class="far fa-filter"
              aria-hidden="true"
            />
          </button>
        </div>

        <vt-report-export-buttons
          source="registry-case-grid"
          :export-report="exportReport"
        />
      </GridHeader>
      <vt-grid
        qa="registry-case-grid"
        class="registry-study-grid"
        :short-key="shortKey"
        :grid-options="gridOptions"
        :column-defs="columnDefs"
        :component-code="gridUiComponent"
      />
    </div>
    <modal-wrapper ref="incidentFormModal">
      <incident-form-modal
        id="incidentFormModal"
        :core-entity="'CASE'"
        @incident-added="reloadRowData"
      />
    </modal-wrapper>

    <modal-wrapper ref="searchPatientModal">
      <search-patient-modal
        id-modal="searchPatientModal"
        @selectedNewCase="onSelectedPatient($event)"
        @selected="onSelectedPatient($event)"
        @openPatientPreviousCases="openPatientPreviousCases($event)"
      />
    </modal-wrapper>

    <modal-wrapper ref="addFilterModal">
      <add-custom-filter-modal
        id="addFilterModal"
        @ok="onAddFilterOk"
      />
    </modal-wrapper>

    <modal-wrapper ref="incidentModalTreeWrapper">
      <common-incident-modal-tree />
    </modal-wrapper>

    <modal-wrapper ref="modalPatientCases">
      <modal-patient-cases id="modalPatientCase" />
    </modal-wrapper>

    <print-component ref="printComponent" />

    <modal-wrapper ref="createCaseModal">
      <create-case-modal
        id="createCaseModal"
        :can-create-cases="true"
        id-modal="searchPatientModal"
        @selectedNewCase="onSelectedPatient($event)"
        @selected="onSelectedPatient($event)"
        @selectedPatientFromHis="onSelectedPatientFromHis"
        @openPatientPreviousCases="openPatientPreviousCases($event)"
      />
    </modal-wrapper>

    <modal-wrapper ref="modalRepeatedPatients">
      <modal-repeated-patients
        :reason-for-showing-modal="$t('component.patientFusion.selectToCreateCase')"
        @patientSelectedFromHis="onSelectedPatientFromHis"
        @patientSelected="createPatientCase"
      />
    </modal-wrapper>
    <modal-wrapper ref="patientSelectionModalWrapper">
      <patient-selection-modal
        id="patientSelectionModal"
        continue-with-case-creation
        @updateLocalPatientAndContinueWithCaseCreation="onUpdateLocalPatientAndContinueWithCaseCreation"
        @continue="createNewPatientCase($event)"
      />
    </modal-wrapper>
    <modal-wrapper ref="reportsModalFormWrapper">
      <reports-modal-form />
    </modal-wrapper>
    <common-attachment-tree-modal
      v-if="selectedPatientForAttachmentTreeModal"
      ref="attachmentTreeModal"
      :patient="selectedPatientForAttachmentTreeModal"
      :laboratory-id="selectedLaboratoryIdForAttachmentTreeModal"
      @attachments-updated="reloadRowData"
    />
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="coreEntity"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="reloadRowData"
      />
    </modal-wrapper>
  </div>
</template>

<script>
import GridHeader from '@/components/GridHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import AddCustomFilterModal from '@/components/customFilter/AddCustomFilterModal.vue'
import SearchPatientModal from '@/components/searcherPatientModal/SearchPatientModal.vue'
import CommonIncidentModalTree from '@/components/incidentsModal/CommonIncidentModalTree.vue'
import CommonAttachmentTreeModal from '@/components/attachments/CommonAttachmentModalTree.vue'
import IncidentFormModal from '@/components/incidentsModal/IncidentFormModal.vue'
import ModalPatientCases from '@/components/patientPreviousCases/ModalPatientCases.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import PatientSelectionModal
    from '@/modules/RegistryModule/components/patientDiscrepancyModal/PatientSelectionModal.vue'
import CreateCaseModal from '@/modules/RegistryModule/components/CreateCaseModal.vue'
import SearcherModuleEntitySelection from '@/components/SearcherModuleEntitySelection.vue'
import ModalRepeatedPatients from '@/modules/RegistryModule/views/editCase/modals/ModalRepeatedPatients.vue'
import ReportsModalForm from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/reports-modal-form.vue'
import AddFileModal from '@/components/AddFileModal.vue'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { GridExportMixin } from '@/mixins/grid-export.mixin'
import { registryCaseGridColumnDefs } from '@/modules/RegistryModule/views/caseGrid/config/registry-case-grid.column-defs'
import { RegistryCaseGridDatasource } from '@/modules/RegistryModule/views/caseGrid/config/registry-case-grid-datasource'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import customFilterResource from '@/api/resources/ui/CustomFilterResource'
import Encoder from '@/utils/encoder'
import studySidebarStore from '@/modules/RegistryModule/sideBar/case/registryCaseSideBarStore'
import sampleServices from '@/modules/RegistryModule/services/sampleServices'
import { isEmpty, isEqual } from 'lodash'
import { registryCasePrintService } from '@/modules/printModule/services/registry-case-print-service'
import { CaseOperation } from '@/api/operations/models/case-operation'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'
import { PATIENT_IDENTIFIER } from '@/api/resources/identifierScopesResource'
import PatientResource from '@/api/resources/PatientResource'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import patientDiscrepancyServices from '@/api/resources/patientDiscrepancyServices'
import registryCaseServices from '@/modules/RegistryModule/services/registryCaseServices'
import { GridUtils } from '@/shared/util/grid-utils'
import commonPatientServices from '@/api/resources/common/commonPatientServices'

export default {
    name: 'RegistryCaseGrid',
    components: {
        GridHeader,
        VtGrid,
        AddCustomFilterModal,
        SearchPatientModal,
        CommonIncidentModalTree,
        CommonAttachmentTreeModal,
        IncidentFormModal,
        ModalPatientCases,
        GridDisplayOptions,
        PatientSelectionModal,
        CreateCaseModal,
        SearcherModuleEntitySelection,
        ModalRepeatedPatients,
        ReportsModalForm,
        AddFileModal
    },
    mixins: [
        CaseOperationGuardMixin,
        IncidentOperationGuardMixin,
        GridExportMixin
    ],
    data() {
        return {
            patient: null,
            pageSize: 20,
            displayOptionsUiComponent: 'uicomponent.registry.casegrid',
            caseOperation: CaseOperation,
            mode: { value: 'compact', icon: 'fal fa-align-justify' },
            gridUiComponent: 'uicomponent.registry.filter.cases',
            patId: null,
            encoder: new Encoder(),
            loadIncidenceForm: false,
            messageProcessModal: '',
            reqCasId: null,
            showSidePanel: false,
            columnDefs: registryCaseGridColumnDefs(),
            gridApi: null,
            columnApi: null,
            sideBarState: studySidebarStore.state,
            selectedRows: [],
            shortKey: { keys: ['alt', 'e'], callFn: this.editCase },
            selectedPatientForAttachmentTreeModal: null,
            selectedLaboratoryIdForAttachmentTreeModal: null,
            entitiesIdArray: []
        }
    },
    computed: {
        coreEntity() {
            return ATTACHMENT_ENTITY_TYPE.CASE
        },
        entitySelectorOptionsForSearch() {
            if (this.hasLabOperationGlobalMixin(this.laboratoryId, CaseOperation.CREATE)) {
                return [
                    { scopes: [IdentifierTypeScope.CENTER_PATIENT, IdentifierTypeScope.PERSON], icon: 'fal fa-user-circle', text: this.$tc('component.searcher.patient'), id: PATIENT_IDENTIFIER },
                    { scopes: [IdentifierTypeScope.CASE, IdentifierTypeScope.SAMPLE], icon: 'fal fa-chart-network', text: this.$tc('registry.searcher.caseOrSamples'), errorMessage: this.$tc('registry.errors.validIdentifierCase') }
                ]
            }
            return [{ scopes: [IdentifierTypeScope.CASE, IdentifierTypeScope.SAMPLE], icon: 'fal fa-chart-network', text: this.$tc('registry.searcher.caseOrSamples'), errorMessage: this.$tc('registry.errors.validIdentifierCase') }]
        },
        gridOptions() {
            return {
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged,
                paginationPageSize: 20,
                paginationAutoPageSize: false,
                getRowHeight: () => this.rowHeight,
                getContextMenuItems: this.getContextMenuItems,
                onRowDoubleClicked: this.onRowClicked,
                sideBar: gridSideBarConfig,
                onSortChanged: ({ api }) => api.deselectAll(),
                onFilterChanged: ({ api }) => api.deselectAll(),
                autoGroupColumnDef: {
                    headerName: 'Consoles', // custom header name for group
                    pinned: 'left', // force pinned left. Does not work in columnDef
                    cellRendererParams: {
                        suppressCount: true // remove number in Group Column
                    }
                }
            }
        },
        laboratoryId() {
            return this.$store.getters.labId
        },
        sideBarFilterModel() {
            if (!this.sideBarState.selectedOption) {
                return {}
            }
            return this.sideBarState.selectedOption.filterModel
        },
        sideBar() {
            // changes when the sideBar is expanded or contracted
            return this.sideBarState.sideBar
        },
        regex() {
            return this.$store.getters.regex
        },
        rowHeight() {
            if (this.mode.value === 'compact') {
                return 25
            }
            return 40
        },
        msgSelectedRows() {
            if (this.selectedRows.length) {
                const choice = this.selectedRows.length > 1 ? 2 : 1
                return `${this.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
            }
            return ''
        }
    },
    watch: {
        sideBarFilterModel(oldValue, newValue) {
            if (!isEqual(oldValue, newValue)) {
                this.applySideBarFilterModel()
            }
        },
        sideBar() {
            if (this.gridApi) {
                this.gridApi.sizeColumnsToFit()
            }
        },
        sideBarState() {
            studySidebarStore.loadCustomOptions()
        }
    },
    methods: {
        showInGlobalLocator(label) {
            this.$eventHub.$emit('show-in-global-locator', label)
        },
        showRegisterIncidentContextMenuOption(caseStudy) {
            return !!caseStudy.casId && this.$_canCreateIncident() && this.$_canBeIncidentCreatedToCase(caseStudy)
        },
        showAddFileModal(params) {
            this.entitiesIdArray = [params.laboratory.labId, params.casId]
            this.$refs.addFileModalWrapper.show()
        },
        showAttachmentModal(params) {
            this.selectedPatientForAttachmentTreeModal = { patId: params.patient.patId }
            this.selectedLaboratoryIdForAttachmentTreeModal = params.laboratory.labId
            this.$nextTick(() => this.$refs.attachmentTreeModal.showCase(params.casId))
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        editCase() {
            if (this.selectedRows.length === 1) {
                this.$store.commit('updateLoading', true)
                this.$router.push({ name: 'registryEditCase', params: { id: this.selectedRows[0].casId } })
            }
        },
        openPatientPreviousCases(patient) {
            this.$refs.modalPatientCases.show(patient)
        },
        onIncidentsClicked(caseStudy) {
            let defaultOption = {
                coreEntity: 'CASE',
                casId: caseStudy.casId
            }
            this.$refs.incidentModalTreeWrapper.show([defaultOption, caseStudy.patient.patId])
        },
        cleanFilters() {
            this.applySideBarFilterModel()
        },
        changedMode(selectedOption) {
            this.mode = selectedOption
            GridUtils.changeRowHeightGrid(this.gridApi, selectedOption.size)
        },
        changedPageSize(pageSize) {
            this.pageSize = pageSize
            this.gridApi.paginationSetPageSize(pageSize)
        },
        getContextMenuItems(params) {
            if (params?.node) {
                const caseStudy = params.node.data
                const isMultipleSelection = this.selectedRows?.length > 1
                return [
                    {
                        name: this.$t('registry.caseGrid.contextMenu.edit'),
                        action: () => this.onRowClicked(params.node),
                        cssClasses: isMultipleSelection ? ['not-show'] : ''
                    },
                    {
                        name: this.$t('registry.caseGrid.contextMenu.viewIncidents'),
                        action: () => this.onIncidentsClicked(caseStudy),
                        cssClasses: isMultipleSelection ? ['not-show'] : ''
                    },
                    {
                        name: this.$t('registry.caseGrid.contextMenu.registerIncident'),
                        action: () => this.openIncidentForm(caseStudy),
                        cssClasses: !isMultipleSelection && this.showRegisterIncidentContextMenuOption(caseStudy) ? '' : ['not-show']
                    },
                    {
                        name: this.$t('laboratory.grid.contextMenu.viewAttach'),
                        action: () => this.showAttachmentModal(caseStudy),
                        cssClasses: isMultipleSelection ? ['not-show'] : ''
                    },
                    {
                        name: this.$t('laboratory.grid.contextMenu.addAttach'),
                        action: () => this.showAddFileModal(caseStudy),
                        cssClasses: isMultipleSelection || !this.$_canAttachDocumentToCase || !this.$_canBeDocumentAttachedToNotValidatedCase(caseStudy) ? ['not-show'] : ''
                    },
                    {
                        name: this.$t('global.showInGlobalLocator'),
                        action: () => this.showInGlobalLocator(caseStudy.casCasenumber),
                        cssClasses: isMultipleSelection ? ['not-show'] : ''
                    }
                ]
            }
            return []
        },
        openIncidentForm(caseStudy) {
            this.$refs.incidentFormModal.show([null, { casId: caseStudy.casId }])
        },
        createNewPatientCase(patient) {
            const patientId = patient.patId | patient.id
            if (patientId) {
                this.$router.push({ name: 'newCase', params: { patId: patientId } })
            }
        },
        async getPatientFromHis(hisPatient) {
            const integrationPatientRequest = this.mapFieldsToRequest(hisPatient)
            this.updateLoading(true)
            const [error, response] = await this.toCall(patientDiscrepancyServices.hisPatientWithDiscrepancy(this.laboratoryId, integrationPatientRequest))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return false
            }
            return response
        },
        async patientWithOrWithoutDiscrepancy(hisPatient) {
            const integrationPatientRequest = this.mapFieldsToRequest(hisPatient)
            this.updateLoading(true)
            const [error, response] = await this.toCall(patientDiscrepancyServices.patientWithOrWithoutDiscrepancy(this.laboratoryId, integrationPatientRequest))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return false
            }
            return response
        },
        async onSelectedPatientFromHis(patient) {
            this.$refs.createCaseModal?.hide()
            const newLocalPatient = await this.patientWithOrWithoutDiscrepancy(patient)
            // patient returned from the above endpoint is the new localPatient
            if (newLocalPatient.withDiscrepancy) {
                const patientFromHis = await this.getPatientFromHis(patient)
                // if the new local patient have discrepancies show modal to resolve
                this.$refs.patientSelectionModalWrapper.show([newLocalPatient, patientFromHis, null, null, null, this.laboratoryId])
            } else {
                // if the new local patient doesn't have discrepancies, continue the process with that patient
                this.createNewPatientCase(newLocalPatient)
            }
        },
        async getPatientById(patId) {
            return this.$_toCallUnique(commonPatientServices.getPatientInformation(this.laboratoryId, patId))
        },
        async onUpdateLocalPatientAndContinueWithCaseCreation(patient) {
            const patientInformation = await this.getPatientById(patient.patId)
            if (patientInformation) {
                await this.createNewPatientCase(patientInformation)
            }
        },
        onSelectedPatient(patient) {
            this.createNewPatientCase(patient)
        },
        mapPersonIdentifiers(personIdentifiers, centerIdentifier, centerPatientIdentifierType, centerIdentifierCenterCode) {
            let mappedIdentifiers = []
            Object.keys(personIdentifiers).forEach(identifier => {
                if (personIdentifiers[identifier].identifier) {
                    mappedIdentifiers.push({
                        identifier: personIdentifiers[identifier].identifier,
                        identifierType: identifier
                    })
                }
            })

            const mappedIdentifier =
                mappedIdentifiers.find(mappedId =>
                    mappedId.identifier === centerIdentifier &&
                    mappedId.identifierType === centerPatientIdentifierType)
            if (!mappedIdentifier && centerIdentifier) {
                mappedIdentifiers.push({
                    centerCode: centerIdentifierCenterCode,
                    identifier: centerIdentifier,
                    identifierType: centerPatientIdentifierType
                })
            }
            return mappedIdentifiers
        },
        mapFieldsToRequest(integrationPatient) {
            return {
                address: {
                    address: integrationPatient.address?.address,
                    postalCode: integrationPatient.address?.postalCode,
                    municipalityCode: integrationPatient.address?.municipality?.code ?? null,
                    provinceCode: integrationPatient.address?.province?.code ?? null,
                    countryCode: integrationPatient.address?.country?.code ?? null
                },
                birthDate: integrationPatient.patBirthdate,
                contactInformation: {
                    email: integrationPatient.contactInformation?.email,
                    mainPhone: integrationPatient.contactInformation?.mainPhone,
                    otherPhone1: integrationPatient.contactInformation?.otherPhone1,
                    otherPhone2: integrationPatient.contactInformation?.otherPhone2
                },
                gender: integrationPatient.genderCode,
                identifiers: this.mapPersonIdentifiers(integrationPatient.personIdentifiers, integrationPatient.centerIdentifier, integrationPatient.centerPatientIdentifierType, integrationPatient.centerIdentifierCenterCode),
                name: integrationPatient.patName,
                surname1: integrationPatient.patSurname1,
                surname2: integrationPatient.patSurname2
            }
        },
        onRowClicked({ data }) {
            this.$store.commit('updateLoading', true)
            this.$router.push({ name: 'registryEditCase', params: { id: data.casId } })
        },
        openReportsModal() {
            this.$refs.reportsModalFormWrapper.show()
        },
        openPatientModal() {
            this.$refs.searchPatientModal.show()
        },
        openCreateCaseModal() {
            this.$refs.createCaseModal.show(false)
        },

        async onSampleSearch(searchInformation) {
            const response =
                await this.$_toCallUnique(
                    sampleServices.searchSampleByLabel(this.laboratoryId, searchInformation),
                    error => this.$_showErrorModal(
                        error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND
                            ? this.$tc('component.searcher.error.noSample')
                            : undefined))

            if (response?.length) {
                let entitySelected = response[0]
                if (response.length > 1) {
                    const foundEntities =
                        response.map(entity => ({
                            entityType: IdentifierTypeScope.SAMPLE,
                            entityValue: entity,
                            laboratoryId: entity.caseStudy.laboratory.labId
                        }))
                    entitySelected = await this.$_showMultiEntitySelection(searchInformation, foundEntities)
                }

                const navigationConfirmed =
                    await this.$_confirmNavigationBecauseOfLaboratoryChange(entitySelected.caseStudy.laboratory.labId)
                if (navigationConfirmed) {
                    await this.$router.push({
                        name: 'registryEditCase',
                        params: {
                            id: entitySelected.caseStudy.casId,
                            samId: entitySelected.samId
                        }
                    })
                }
            }
        },
        async onStudySearch(searchInformation) {
            const response =
                await this.$_toCallUnique(
                    registryCaseServices.findCaseByLabel(this.laboratoryId, searchInformation),
                    error => {
                        if (error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND) {
                            this.$_showErrorModal(this.$tc('component.searcher.error.noCase'))
                        } else {
                            this.$_showErrorModalFromErrorResponse(error)
                        }
                    })

            if (response?.length) {
                let entitySelected = response[0]
                if (response.length > 1) {
                    const foundEntities =
                        response.map(entity => ({
                            entityType: IdentifierTypeScope.CASE,
                            entityValue: entity,
                            laboratoryId: entity.laboratory.labId
                        }))
                    entitySelected = await this.$_showMultiEntitySelection(searchInformation, foundEntities)
                }

                const navigationConfirmed =
                    await this.$_confirmNavigationBecauseOfLaboratoryChange(entitySelected.laboratory.labId)
                if (navigationConfirmed) {
                    await this.$router.push({
                        name: 'registryEditCase',
                        params: {
                            id: entitySelected.casId
                        }
                    })
                }
            }
        },
        async onPatientSearch(searchInformation) {
            const params = {
                size: PAGINATOR_BIG_PAGE_SIZE,
                id: searchInformation.identifierSearch,
                surname1: searchInformation.patientSurname1Search,
                surname2: searchInformation.patientSurmname2Search,
                middleName: searchInformation.patientMiddlenameSearch,
                requestingLaboratoryId: this.$store.getters.labId,
                name: searchInformation.patientNameSearch,
                centerCode: searchInformation.center,
                identifierType: searchInformation.type
            }
            const response =
                await this.$_toCallUnique(
                    PatientResource.paginatorMultiSourced(params),
                    error => this.$_showErrorModal(
                        error.response.data.code === this.$apiExceptions.RESULT_LIMIT_EXCEEDED
                            ? this.$tc('component.patient.searcher.tooManySearchResults')
                            : undefined))

            const patients = response.content
            if (!patients.length) {
                this.$_showErrorModal(this.$tc('component.patient.searcher.noResults'))
            } else if (patients.length > 1) {
                this.$refs.modalRepeatedPatients.show([patients, searchInformation.identifierSearch])
            } else {
                if (!patients[0].patId) {
                    await this.onSelectedPatientFromHis(patients[0])
                } else {
                    this.createPatientCase(patients[0])
                }
            }
        },

        createPatientCase(patient) {
            const patientId = patient.patId
            this.$router.push({ name: 'newCase', params: { patId: patientId } })
        },
        async onSideBarClickUpdateFilter(option) {
            if (isEmpty(this.gridApi.getFilterModel())) {
                this.saveNoFilter()
                return
            }
            const [err] = await this.toCall(studySidebarStore.updateCustomOption(option, { filterModel: this.gridApi.getFilterModel() }))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            await studySidebarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterUpdated'))
        },
        reloadRowData() {
            this.gridApi.deselectAll()
            this.gridApi.setServerSideDatasource(new RegistryCaseGridDatasource(this.laboratoryId))
        },
        onGridReady(api) {
            this.gridApi = api.api
            this.columnApi = api.columnApi

            this.gridApi.setColumnDefs(
                this.$_filterPatientColumns(
                    this.columnDefs,
                    'patient.patMiddleName',
                    'patient.patSurname2'))
            if (this.sideBarFilterModel && !this.$route.params.showPatientCases) {
                this.applySideBarFilterModel()
            }

            this.gridApi.setServerSideDatasource(new RegistryCaseGridDatasource(this.laboratoryId))
        },
        applySideBarFilterModel() {
            if (this.gridApi && !isEmpty(this.sideBarFilterModel)) {
                this.columnDefs = registryCaseGridColumnDefs(this.sideBarFilterModel['casRegistrationDate'] !== undefined)
                this.gridApi.setFilterModel(this.sideBarFilterModel)
                this.gridApi.deselectAll()
            }
        },
        onSideBarClickAddFilter() {
            if (isEmpty(this.gridApi.getFilterModel())) {
                this.saveNoFilter()
                return
            }
            this.$refs.addFilterModal.show()
        },
        async onAddFilterOk(filter) {
            const params = {
                label: filter.label,
                filterModel: this.gridApi.getFilterModel()
            }

            await customFilterResource.create(
                this.sideBarState.componentCode,
                params
            )
            await studySidebarStore.loadCustomOptions()
            this.showToastSuccessGlobal(this.$t('global.filterAdded'))
        },
        exportReport(exportType) {
            const columns = this.$_getColumnsToExport(this.gridApi, this.columnApi)
            if (columns && this.gridApi) {
                const params = {
                    exportType,
                    sort: GridUtils.getSortValue(this.gridApi.getSortModel()),
                    page: 0,
                    size: PAGINATOR_BIG_PAGE_SIZE
                }
                const entityIdentifiers = this.selectedRows.map(selectedRow => selectedRow.casId)
                const request = {
                    sectionLabel: this.$tc('global.register'),
                    filterModel: this.encoder.parseBooleans(this.gridApi.getFilterModel()),
                    columns,
                    entityIdentifiersRequest: { entityIdentifiers }
                }
                return registryCasePrintService.exportTo(this.laboratoryId, params, request)
            }
            return null
        }
    }
}
</script>

<style>

.registry-study-grid {
  height: 100%;
}

.registry-study-grid .registry-request-page {
  display: flex;
}

.registry-study-grid .sr-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.5rem;
  flex-shrink: 0;
}

.registry-study-grid .sr-header-buttons {
  margin-right: 1.5em;
}

</style>
